import { createClient } from "@/lib/supabase/client";

export type NotificationFrequency = "instant" | "hourly" | "daily";

export interface ChannelToggles {
  email: boolean;
  toast: boolean;
  sms: boolean;
}

export interface NotificationPreferences {
  id?: string;
  userId: string;
  role: "admin" | "owner";

  // Global channel toggles
  emailEnabled: boolean;
  toastEnabled: boolean;
  smsEnabled: boolean;

  // Channel frequencies
  emailFrequency: NotificationFrequency;
  toastFrequency: NotificationFrequency;
  smsFrequency: NotificationFrequency;

  // Admin events
  adminNewLead: ChannelToggles;
  adminCleanerApplication: ChannelToggles;
  adminLeadStatusChange: ChannelToggles;
  adminNewOwnerMessage: ChannelToggles;
  adminDailyDigest: ChannelToggles;

  // Owner events
  ownerAdminReply: ChannelToggles;
  ownerBookingConfirmation: ChannelToggles;
  ownerServiceRequestUpdate: ChannelToggles;
  ownerCleaningReminder: ChannelToggles;
  ownerInvoiceGenerated: ChannelToggles;

  // SMS
  smsPhone: string;
}

const DEFAULT_CHANNEL: ChannelToggles = { email: true, toast: true, sms: false };
const DEFAULT_DIGEST: ChannelToggles = { email: true, toast: false, sms: false };

export function getDefaultPreferences(userId: string, role: "admin" | "owner"): NotificationPreferences {
  return {
    userId,
    role,
    emailEnabled: true,
    toastEnabled: true,
    smsEnabled: false,
    emailFrequency: "instant",
    toastFrequency: "instant",
    smsFrequency: "instant",
    adminNewLead: { ...DEFAULT_CHANNEL },
    adminCleanerApplication: { ...DEFAULT_CHANNEL },
    adminLeadStatusChange: { ...DEFAULT_CHANNEL },
    adminNewOwnerMessage: { ...DEFAULT_CHANNEL },
    adminDailyDigest: { ...DEFAULT_DIGEST },
    ownerAdminReply: { ...DEFAULT_CHANNEL },
    ownerBookingConfirmation: { ...DEFAULT_CHANNEL },
    ownerServiceRequestUpdate: { ...DEFAULT_CHANNEL },
    ownerCleaningReminder: { ...DEFAULT_CHANNEL },
    ownerInvoiceGenerated: { ...DEFAULT_CHANNEL },
    smsPhone: "",
  };
}

// ─── DB row → app type ───
function rowToPrefs(row: Record<string, unknown>): NotificationPreferences {
  const ch = (v: unknown): ChannelToggles => {
    const obj = (v ?? {}) as Record<string, boolean>;
    return { email: obj.email ?? true, toast: obj.toast ?? true, sms: obj.sms ?? false };
  };
  return {
    id: row.id as string,
    userId: row.user_id as string,
    role: (row.role as "admin" | "owner") ?? "owner",
    emailEnabled: (row.email_enabled as boolean) ?? true,
    toastEnabled: (row.toast_enabled as boolean) ?? true,
    smsEnabled: (row.sms_enabled as boolean) ?? false,
    emailFrequency: (row.email_frequency as NotificationFrequency) ?? "instant",
    toastFrequency: (row.toast_frequency as NotificationFrequency) ?? "instant",
    smsFrequency: (row.sms_frequency as NotificationFrequency) ?? "instant",
    adminNewLead: ch(row.admin_new_lead),
    adminCleanerApplication: ch(row.admin_cleaner_application),
    adminLeadStatusChange: ch(row.admin_lead_status_change),
    adminNewOwnerMessage: ch(row.admin_new_owner_message),
    adminDailyDigest: ch(row.admin_daily_digest),
    ownerAdminReply: ch(row.owner_admin_reply),
    ownerBookingConfirmation: ch(row.owner_booking_confirmation),
    ownerServiceRequestUpdate: ch(row.owner_service_request_update),
    ownerCleaningReminder: ch(row.owner_cleaning_reminder),
    ownerInvoiceGenerated: ch(row.owner_invoice_generated),
    smsPhone: (row.sms_phone as string) ?? "",
  };
}

// ─── App type → DB row ───
function prefsToRow(prefs: NotificationPreferences): Record<string, unknown> {
  return {
    user_id: prefs.userId,
    role: prefs.role,
    email_enabled: prefs.emailEnabled,
    toast_enabled: prefs.toastEnabled,
    sms_enabled: prefs.smsEnabled,
    email_frequency: prefs.emailFrequency,
    toast_frequency: prefs.toastFrequency,
    sms_frequency: prefs.smsFrequency,
    admin_new_lead: prefs.adminNewLead,
    admin_cleaner_application: prefs.adminCleanerApplication,
    admin_lead_status_change: prefs.adminLeadStatusChange,
    admin_new_owner_message: prefs.adminNewOwnerMessage,
    admin_daily_digest: prefs.adminDailyDigest,
    owner_admin_reply: prefs.ownerAdminReply,
    owner_booking_confirmation: prefs.ownerBookingConfirmation,
    owner_service_request_update: prefs.ownerServiceRequestUpdate,
    owner_cleaning_reminder: prefs.ownerCleaningReminder,
    owner_invoice_generated: prefs.ownerInvoiceGenerated,
    sms_phone: prefs.smsPhone,
  };
}

export async function loadNotificationPreferences(
  userId: string,
  role: "admin" | "owner"
): Promise<NotificationPreferences> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from("notification_preferences")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    console.warn("[loadNotificationPreferences] error:", error.message);
    return getDefaultPreferences(userId, role);
  }
  if (!data) return getDefaultPreferences(userId, role);
  return rowToPrefs(data as Record<string, unknown>);
}

export async function saveNotificationPreferences(
  prefs: NotificationPreferences
): Promise<{ success: boolean; error?: string }> {
  const supabase = createClient();
  const row = prefsToRow(prefs);

  const { error } = await supabase
    .from("notification_preferences")
    .upsert(row, { onConflict: "user_id" });

  if (error) {
    console.warn("[saveNotificationPreferences] error:", error.message);
    return { success: false, error: error.message };
  }
  return { success: true };
}

// ─── Guard helpers used by email/toast/SMS callers ───
export function canSendEmail(
  prefs: NotificationPreferences | null,
  eventKey: keyof Pick<
    NotificationPreferences,
    | "adminNewLead" | "adminCleanerApplication" | "adminLeadStatusChange" |"adminNewOwnerMessage"| "adminDailyDigest" |"ownerAdminReply"| "ownerBookingConfirmation" | "ownerServiceRequestUpdate" |"ownerCleaningReminder" | "ownerInvoiceGenerated"
  >
): boolean {
  if (!prefs) return true; // default allow if prefs not loaded
  if (!prefs.emailEnabled) return false;
  return (prefs[eventKey] as ChannelToggles).email;
}

export function canShowToast(
  prefs: NotificationPreferences | null,
  eventKey: keyof Pick<
    NotificationPreferences,
    | "adminNewLead" | "adminCleanerApplication" | "adminLeadStatusChange" |"adminNewOwnerMessage"| "adminDailyDigest" |"ownerAdminReply"| "ownerBookingConfirmation" | "ownerServiceRequestUpdate" |"ownerCleaningReminder" | "ownerInvoiceGenerated"
  >
): boolean {
  if (!prefs) return true;
  if (!prefs.toastEnabled) return false;
  return (prefs[eventKey] as ChannelToggles).toast;
}

export function canSendSMS(
  prefs: NotificationPreferences | null,
  eventKey: keyof Pick<
    NotificationPreferences,
    | "adminNewLead" | "adminCleanerApplication" | "adminLeadStatusChange" |"adminNewOwnerMessage"| "adminDailyDigest" |"ownerAdminReply"| "ownerBookingConfirmation" | "ownerServiceRequestUpdate" |"ownerCleaningReminder" | "ownerInvoiceGenerated"
  >
): boolean {
  if (!prefs) return false;
  if (!prefs.smsEnabled) return false;
  if (!prefs.smsPhone) return false;
  return (prefs[eventKey] as ChannelToggles).sms;
}
